import { app } from '../config/firebase-config';

// isSupported() is async and returns false in environments where Analytics
// can't run (some in-app browsers, blocked storage), so every call funnels
// through one cached promise and no-ops rather than throwing.
let instance;

// gtag is ~160 KB and nothing on the page waits for it, so it loads once the
// browser is idle. Events fired before then queue on the same promise.
const whenIdle = () => new Promise((resolve) => {
    if (typeof window.requestIdleCallback === 'function') {
        window.requestIdleCallback(() => resolve(), { timeout: 4000 });
    } else {
        setTimeout(resolve, 2000);
    }
});

// Local builds and dev servers never report: they were showing up in GA as
// real visits.
const isLocal = () =>
    typeof window !== 'undefined' &&
    /^(localhost|127\.0\.0\.1|\[::1\]|.*\.local)$/.test(window.location.hostname);

const resolveAnalytics = () => {
    if (!instance) {
        instance = isLocal() ? Promise.resolve(null) : whenIdle()
            .then(() => import('firebase/analytics'))
            .then(async (mod) => ({ mod, ok: await mod.isSupported() }))
            .then(({ mod, ok }) => (ok ? { mod, analytics: mod.getAnalytics(app) } : null))
            .catch(() => null);
    }
    return instance;
};

/** Fire-and-forget event. Analytics must never break a user interaction. */
export const track = (name, params = {}) => {
    resolveAnalytics()
        .then((analytics) => {
            if (analytics) analytics.mod.logEvent(analytics.analytics, name, params);
        })
        .catch(() => { /* analytics is optional; ignore */ });
};

/**
 * Route changes need logging by hand: this is a single-page app.
 * The page's own <title> lands after the route effect runs (and after a lazy
 * chunk loads), while the static one from index.html is still first in the
 * head, so document.title would report the homepage title for every route.
 * Wait briefly for the route's title before sending.
 */
export const trackPageView = (path) => {
    const location = window.location.href;
    let tries = 0;
    const attempt = () => {
        const title = document.querySelector('title:not([data-static])')?.textContent;
        if (title || tries++ >= 10) {
            track('page_view', { page_path: path, page_location: location, page_title: title || document.title });
        } else {
            setTimeout(attempt, 150);
        }
    };
    setTimeout(attempt, 0);
};
